"use client";
import { Button } from "@workspace/ui/components";
import { cn } from "@workspace/ui/lib/utils";
import { CircleCheck } from "lucide-react";
import Link from "next/link";
import salesManagers from "@/public/animations/2-1.json";
import salesOperations from "@/public/animations/2-2.json";
import cro from "@/public/animations/2-3.json";
import actionableFeedback from "@/public/animations/2-4.json";
import Lottie from "lottie-react";
import { LottieInView } from "@workspace/ui/common";

const ITEMS = [
  {
    title: "Sales Managers",
    animation: salesManagers,
    description:
      "Stop scrubbing transcripts. Yolk gives you a live skills map and auto-builds the drills – so you coach what matters and move the number.",
    features: [
      "Know Exactly Who To Coach And On What, Instantly",
      "Save 5+ Hours/Week",
      "Auto-Deploy Targeted AI Roleplays",
      "Win Back Time For Strategy, Deals, And Pipeline",
    ],
    action: { name: "Start Free", href: "/auth" },
  },
  {
    title: "Enablement Managers",
    animation: actionableFeedback,
    description:
      "Stop building training from scratch. Yolk turns real call data into AI roleplays, tracks completion and proficiency, and proves impact – so you can scale great coaching without extra headcount.",
    features: [
      "Automate training creation with AI",
      "Ensure reps complete assigned training",
      "Measure training impact with built-in analytics",
      "Spend less time building, more time scaling",
    ],
    action: { name: "Automate Training", href: "/auth" },
  },
  {
    title: "Sales Operations Directors",
    animation: salesOperations,
    description:
      "Gain visibility into performance drivers and coaching quality. Yolk standardizes call scoring, shortens rep ramp time, and gives you skill-level insights to optimize processes across the entire organization.",
    features: [
      "Consistent coaching across managers",
      "Predictable ramp times for new hires",
      "Understand why deals are won or lost",
      "Scale enablement without new headcount",
    ],
    action: { name: "See Team Analytics", href: "/auth" },
  },
  {
    title: "CROs & VPs of Sales",
    animation: cro,
    description:
      "Hit revenue targets faster with scalable coaching. Yolk replaces generic training with rep-specific development that shortens ramp time, boosts win rates, and lowers enablement costs.",
    features: [
      "Cut ramp times and hit quota faster",
      "Improve win rates with data-driven coaching",
      "Lower enablement costs with automation",
      "Unlock growth without increasing headcount",
    ],
    action: { name: "Boost Win Rates", href: "/auth" },
  },
];
export const Features = () => {
  return (
    <div className="flex flex-col items-center w-full gap-10 md:gap-[72px]">
      {ITEMS.map((item, i) => (
        <div
          key={i}
          className={cn(
            "flex flex-col gap-5 max-w-[1144px] w-full lg:gap-[96px] items-center",
            i % 2 === 0 ? "md:flex-row" : "md:flex-row-reverse",
          )}
        >
          <div className="flex items-center justify-center rounded-[32px] border border-[#e3d8c4] aspect-[4/3] w-full md:max-w-[562px] bg-[#F3E4C9]">
            <LottieInView
              animationData={item.animation}
              width={562}
              height={428}
            />
          </div>
          <div className="flex flex-col md:max-w-[428px] w-full">
            <span className="text-lg lg:text-2xl font-semibold leading-6 mb-1.5 lg:mb-3">
              {item.title}
            </span>
            <p className="text-sm font-medium leading-5 text-d-gray mb-3">
              {item.description}
            </p>
            <ul className="list-none gap-2 flex flex-col mb-6">
              {item.features.map((f, j) => (
                <li key={j} className="flex items-center gap-2.5 capitalize">
                  <CircleCheck
                    size={20}
                    className="[&>circle]:fill-d-green [&>circle]:stroke-d-green [&>path]:stroke-white"
                  />
                  <span className="text-sm font-medium leading-5">{f}</span>
                </li>
              ))}
            </ul>
            <Button variant="d-primary" className="h-12" asChild>
              <Link href={item.action.href}>{item.action.name}</Link>
            </Button>
          </div>
        </div>
      ))}
    </div>
  );
};
